import { api } from '../api/client'

/**
 * OS-Push-Anmeldung im Browser (zweiter Kanal neben der In-App-Zentrale). Alles best-effort: fehlt eine API,
 * verweigert der Nutzer, oder ist Push serverseitig nicht konfiguriert (kein VAPID-Key), passiert einfach nichts —
 * die In-App-Glocke funktioniert unabhängig davon. iOS/iPadOS: nur in der INSTALLIERTEN PWA verfügbar (16.4+).
 */

/** base64url → Uint8Array (für applicationServerKey). */
export function urlBase64ToUint8Array(base64url: string): Uint8Array {
  const pad = base64url.length % 4 === 0 ? '' : '='.repeat(4 - (base64url.length % 4))
  const base64 = (base64url + pad).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/** Unterstützt dieser Browser Web-Push (SW + PushManager + Notification)? */
export function isPushSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof window !== 'undefined' && 'PushManager' in window && 'Notification' in window
}

/** Aktueller Erlaubnis-Stand (oder 'unsupported'). */
export function pushPermission(): NotificationPermission | 'unsupported' {
  return isPushSupported() ? Notification.permission : 'unsupported'
}

/**
 * Meldet dieses Gerät für Push an und verknüpft es serverseitig mit dem AKTUELLEN Konto (Upsert auf endpoint).
 * `interactive=true` fragt bei Bedarf die Erlaubnis (nur aus einer User-Geste erlauben!); `false` = stiller
 * Abgleich beim Login (nur wenn bereits erlaubt) → wichtig auf geteilten Tablets, damit das Gerät dem neuen
 * Konto gehört. Rückgabe: true, wenn eine aktive Subscription am Server hinterlegt wurde.
 */
export async function subscribePush(interactive = true): Promise<boolean> {
  if (!isPushSupported()) return false
  if (!interactive && Notification.permission !== 'granted') return false
  const { key } = await api.vapidPublicKey()
  if (!key) return false // Push serverseitig nicht konfiguriert
  const permission = interactive ? await Notification.requestPermission() : Notification.permission
  if (permission !== 'granted') return false
  const reg = await navigator.serviceWorker.ready
  const existing = await reg.pushManager.getSubscription()
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // Cast überbrückt die TS-lib-Strenge (Uint8Array<ArrayBufferLike> vs. BufferSource); Laufzeit ist korrekt.
      applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
    }))
  const j = sub.toJSON()
  if (!j.endpoint || !j.keys?.p256dh || !j.keys?.auth) return false
  await api.pushSubscribe({ endpoint: j.endpoint, keys: { p256dh: j.keys.p256dh, auth: j.keys.auth } })
  return true
}

/** Entfernt die Server-Zuordnung dieses Geräts (Abmeldung/Logout). Die Browser-Subscription bleibt für
 *  eine spätere Wiederverwendung erhalten (nächstes Konto übernimmt sie per Upsert). Best-effort. */
export async function unsubscribePush(): Promise<void> {
  if (!isPushSupported()) return
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (sub) await api.pushUnsubscribe({ endpoint: sub.endpoint }).catch(() => {})
  } catch {
    // best-effort — Logout darf daran nie scheitern.
  }
}
